/**
 * QueryPatterns.jsx
 * Define query patterns for large tables - date formats, required filters, etc.
 * Example: For payment_txns, use createdAt > '2026-01-01 18:30:00' instead of DATE()
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { 
  Database, Table2, Calendar, AlertTriangle, Check, X,
  ChevronRight, Search, Save, RefreshCw, CheckCircle2,
  Clock, Filter, Code, Trash2, HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getTableQueryPatterns, saveTableQueryPattern, deleteTableQueryPattern
} from '../../services/api';

const QueryPatterns = ({ onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const [state, setState] = useState({
    loading: true,
    patterns: [],
    largeTables: [],
    currentTable: null,
    editingPattern: {
      date_format: "'YYYY-MM-DD HH:MM:SS'",
      required_filters: [],
      avoid_functions: [],
      sample_query: '',
      max_days_back: 90
    },
    notes: '',
    searchQuery: '',
    showConfigured: false
  });

  const loadData = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true }));
    try {
      const result = await getTableQueryPatterns();
      
      // Create lookup for existing patterns
      const patternLookup = {};
      for (const p of result.patterns || []) {
        const key = `${p.server}|${p.database}|${p.table}`;
        patternLookup[key] = p;
      }

      setState(prev => ({
        ...prev,
        loading: false,
        patterns: result.patterns || [],
        largeTables: result.largeTables || [],
        patternLookup
      }));

      // Calculate progress
      const configured = result.totalPatterns || 0;
      const total = result.largeTables?.length || 1;
      onCompleteRef.current(Math.min(100, Math.round((configured / Math.max(1, total)) * 100)));

    } catch (error) {
      console.error('Failed to load patterns:', error);
      toast.error('Failed to load query patterns');
      setState(prev => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectTable = (table) => {
    const key = `${table.server}|${table.database}|${table.table}`;
    const existingPattern = state.patternLookup?.[key];

    setState(prev => ({
      ...prev,
      currentTable: table,
      editingPattern: existingPattern?.patterns || {
        date_format: "'YYYY-MM-DD HH:MM:SS'",
        required_filters: table.dateColumns?.[0] ? [table.dateColumns[0]] : [],
        avoid_functions: ['DATE()', 'NOW()', 'CURDATE()'],
        sample_query: `SELECT * FROM ${table.table}\nWHERE ${table.dateColumns?.[0] || 'created_at'} > '2026-01-01 00:00:00'\nLIMIT 1000`,
        max_days_back: 90
      },
      notes: existingPattern?.notes || ''
    }));
  };

  const handleSave = async () => {
    if (!state.currentTable) return;

    try {
      await saveTableQueryPattern({
        server: state.currentTable.server,
        database: state.currentTable.database,
        table: state.currentTable.table,
        patterns: state.editingPattern,
        notes: state.notes
      });

      // Update local state
      const key = `${state.currentTable.server}|${state.currentTable.database}|${state.currentTable.table}`;
      setState(prev => ({
        ...prev,
        patternLookup: {
          ...prev.patternLookup,
          [key]: {
            ...state.currentTable,
            patterns: state.editingPattern,
            notes: state.notes
          }
        },
        largeTables: prev.largeTables.map(t =>
          t.server === state.currentTable.server &&
          t.database === state.currentTable.database &&
          t.table === state.currentTable.table
            ? { ...t, hasPattern: true }
            : t
        ),
        currentTable: null
      }));

      toast.success(`Query pattern saved for ${state.currentTable.table}. AI will use these rules!`);
      loadData(); // Refresh counts

    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save pattern');
    }
  };

  const handleDelete = async (table) => {
    if (!window.confirm(`Delete query pattern for ${table.table}?`)) return;

    try {
      await deleteTableQueryPattern(table.server, table.database, table.table);

      const key = `${table.server}|${table.database}|${table.table}`;
      setState(prev => {
        const newLookup = { ...prev.patternLookup };
        delete newLookup[key];
        return {
          ...prev,
          patternLookup: newLookup,
          largeTables: prev.largeTables.map(t =>
            t.server === table.server &&
            t.database === table.database &&
            t.table === table.table
              ? { ...t, hasPattern: false }
              : t
          )
        };
      });

      toast.success('Pattern deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const toggleRequiredFilter = (column) => {
    setState(prev => {
      const current = prev.editingPattern.required_filters || [];
      const updated = current.includes(column)
        ? current.filter(c => c !== column)
        : [...current, column];
      return {
        ...prev,
        editingPattern: { ...prev.editingPattern, required_filters: updated }
      };
    });
  };

  const filteredTables = state.largeTables.filter(table => {
    const matchesSearch = !state.searchQuery ||
      table.table.toLowerCase().includes(state.searchQuery.toLowerCase()) ||
      table.database.toLowerCase().includes(state.searchQuery.toLowerCase());
    
    const matchesFilter = state.showConfigured ? table.hasPattern : !table.hasPattern;
    
    return matchesSearch && matchesFilter;
  });

  const configuredCount = state.largeTables.filter(t => t.hasPattern).length;
  const pendingCount = state.largeTables.length - configuredCount;

  const formatRowCount = (count) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
    if (count >= 1000) return `${(count / 1000).toFixed(0)}K`;
    return count;
  };

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-purple-500 mr-2" />
        <span className="text-gray-500">Finding large tables...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Query Patterns for Large Tables</h3>
          <p className="text-sm text-gray-500">
            Define how AI should query large tables (date formats, required filters, etc.)
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm">
            <span className="text-green-600 font-medium">{configuredCount}</span>
            <span className="text-gray-400"> configured / </span>
            <span className="text-amber-600 font-medium">{pendingCount}</span>
            <span className="text-gray-400"> pending</span>
          </div>
          <Button variant="outline" size="sm" onClick={loadData}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Info box */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <HelpCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Why configure query patterns?</p>
            <p className="text-blue-600">
              Large tables can break queries if not filtered properly. For example:
            </p>
            <ul className="list-disc ml-4 mt-1 text-blue-600">
              <li><code className="bg-blue-100 px-1 rounded">createdAt &gt; DATE()</code> might fail</li>
              <li><code className="bg-blue-100 px-1 rounded">createdAt &gt; '2026-01-01 18:30:00'</code> works correctly</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <Progress value={(configuredCount / Math.max(1, state.largeTables.length)) * 100} className="h-2" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Table List */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                Large Tables (100K+ rows)
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant={!state.showConfigured ? "default" : "outline"}
                  size="sm"
                  onClick={() => setState(prev => ({ ...prev, showConfigured: false }))}
                >
                  Pending ({pendingCount})
                </Button>
                <Button
                  variant={state.showConfigured ? "default" : "outline"}
                  size="sm"
                  onClick={() => setState(prev => ({ ...prev, showConfigured: true }))}
                >
                  Configured ({configuredCount})
                </Button>
              </div>
            </div>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search tables..."
                value={state.searchQuery}
                onChange={(e) => setState(prev => ({ ...prev, searchQuery: e.target.value }))}
                className="pl-9"
              />
            </div>
          </CardHeader>
          <CardContent className="max-h-[500px] overflow-y-auto">
            {filteredTables.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                {state.showConfigured ? 'No configured patterns yet' : 'All large tables have patterns!'}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredTables.map((table, idx) => {
                  const key = `${table.server}|${table.database}|${table.table}`;
                  const isSelected = state.currentTable &&
                    state.currentTable.server === table.server &&
                    state.currentTable.database === table.database &&
                    state.currentTable.table === table.table;

                  return (
                    <div
                      key={key}
                      onClick={() => selectTable(table)}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        isSelected
                          ? 'border-purple-400 bg-purple-50'
                          : table.hasPattern
                            ? 'border-green-200 bg-green-50 hover:border-green-300'
                            : 'border-amber-200 bg-amber-50/50 hover:border-purple-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Table2 className={`w-4 h-4 ${table.hasPattern ? 'text-green-500' : 'text-amber-500'}`} />
                          <span className="font-medium text-gray-800">{table.table}</span>
                          <Badge variant="outline" className="text-xs bg-red-50 text-red-600 border-red-200">
                            {formatRowCount(table.rowCount)} rows
                          </Badge>
                        </div>
                        {table.hasPattern ? (
                          <div className="flex items-center gap-1">
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); handleDelete(table); }}
                              className="h-6 w-6 p-0 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                        <Database className="w-3 h-3" />
                        <span>{table.server}.{table.database}</span>
                        {table.dateColumns?.length > 0 && (
                          <>
                            <Calendar className="w-3 h-3 ml-2" />
                            <span>{table.dateColumns.join(', ')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pattern Editor */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">
              {state.currentTable ? (
                <span>Configure: <span className="text-purple-600">{state.currentTable.table}</span></span>
              ) : (
                'Select a table to configure'
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!state.currentTable ? (
              <div className="text-center py-12 text-gray-400">
                <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Select a large table from the list to configure query patterns</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Table info */}
                <div className="p-3 bg-gray-50 rounded-lg text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">
                      {state.currentTable.server}.{state.currentTable.database}.
                      <span className="font-medium text-purple-600">{state.currentTable.table}</span>
                    </span>
                    <Badge variant="outline" className="bg-red-50 text-red-600">
                      {formatRowCount(state.currentTable.rowCount)} rows
                    </Badge>
                  </div>
                </div>

                {/* Date format */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Date Format (how to write dates in queries)
                  </label>
                  <Input
                    placeholder="'YYYY-MM-DD HH:MM:SS'"
                    value={state.editingPattern.date_format || ''}
                    onChange={(e) => setState(prev => ({
                      ...prev,
                      editingPattern: { ...prev.editingPattern, date_format: e.target.value }
                    }))}
                    className="mt-1 font-mono"
                  />
                  <p className="text-xs text-gray-400 mt-1">Example: '2026-01-15 18:30:00'</p>
                </div>

                {/* Required filters */}
                {state.currentTable.dateColumns?.length > 0 && (
                  <div>
                    <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <Filter className="w-4 h-4" />
                      Required Filters (must be in WHERE clause)
                    </label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {state.currentTable.dateColumns.map(col => (
                        <Badge
                          key={col}
                          variant={state.editingPattern.required_filters?.includes(col) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => toggleRequiredFilter(col)}
                        >
                          {state.editingPattern.required_filters?.includes(col) && (
                            <Check className="w-3 h-3 mr-1" />
                          )}
                          {col}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Max days back */}
                <div>
                  <label className="text-sm font-medium text-gray-700">
                    Max Days Back (default date range)
                  </label>
                  <Input
                    type="number"
                    placeholder="90"
                    value={state.editingPattern.max_days_back || ''}
                    onChange={(e) => setState(prev => ({
                      ...prev,
                      editingPattern: { ...prev.editingPattern, max_days_back: parseInt(e.target.value) || 90 }
                    }))}
                    className="mt-1 w-32"
                  />
                  <p className="text-xs text-gray-400 mt-1">AI will default to last N days if no date specified</p>
                </div>

                {/* Sample query */}
                <div>
                  <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Code className="w-4 h-4" />
                    Sample Query (show AI the correct pattern)
                  </label>
                  <Textarea
                    placeholder={`SELECT * FROM ${state.currentTable.table}\nWHERE createdAt > '2026-01-01 00:00:00'\nLIMIT 1000`}
                    value={state.editingPattern.sample_query || ''}
                    onChange={(e) => setState(prev => ({
                      ...prev,
                      editingPattern: { ...prev.editingPattern, sample_query: e.target.value }
                    }))}
                    className="mt-1 font-mono text-sm"
                    rows={4}
                  />
                </div>

                {/* Notes */}
                <div>
                  <label className="text-sm font-medium text-gray-700">Additional Notes</label>
                  <Input
                    placeholder="e.g., Always filter by createdAt first for performance"
                    value={state.notes}
                    onChange={(e) => setState(prev => ({ ...prev, notes: e.target.value }))}
                    className="mt-1"
                  />
                </div>

                {/* Save button */}
                <div className="flex gap-2">
                  <Button onClick={handleSave} className="flex-1">
                    <Save className="w-4 h-4 mr-2" />
                    Save Pattern
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setState(prev => ({ ...prev, currentTable: null }))}
                  >
                    Cancel
                  </Button>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  Patterns are shared across all admin users
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QueryPatterns;
